const GOOGLE_OIDC_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const GOOGLE_OIDC_CALLBACK_PATH = "/google-oidc-callback.html";
const GOOGLE_OIDC_MESSAGE_SOURCE = "smart-wallet-google-oidc";
const GOOGLE_OIDC_SCOPE = "openid email";
const POPUP_POLL_INTERVAL_MS = 500;
const POPUP_CLOSE_GRACE_MS = 2_000;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;

interface GoogleOidcMessage {
  source?: string;
  state?: string;
  idToken?: string;
  error?: string;
  errorDescription?: string;
}

export function requestGoogleIdToken(args: { clientId: string }): Promise<string> {
  const clientId = args.clientId.trim();
  if (clientId === "") {
    return Promise.reject(new Error("Google client ID is not configured"));
  }

  const state = randomBase64Url(16);
  const nonce = randomBase64Url(16);
  const redirectUri = new URL(GOOGLE_OIDC_CALLBACK_PATH, window.location.origin).toString();
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "id_token",
    scope: GOOGLE_OIDC_SCOPE,
    state,
    nonce,
    prompt: "select_account",
  });
  const authUrl = `${GOOGLE_OIDC_AUTH_URL}?${params.toString()}`;
  const popup = window.open(authUrl, "smart-wallet-google", popupFeatures());
  if (popup === null) {
    return Promise.reject(new Error("Google sign-in popup was blocked"));
  }
  popup.focus();

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let settled = false;
    let popupPoll: number | undefined;
    let closedAt: number | null = null;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      if (popupPoll !== undefined) {
        window.clearInterval(popupPoll);
      }
      if (!popup.closed) {
        popup.close();
      }
    };
    const finish = (result: { token?: string; error?: Error }) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      if (result.error !== undefined) {
        reject(result.error);
      } else if (result.token !== undefined) {
        resolve(result.token);
      } else {
        reject(new Error("Google did not return an ID token"));
      }
    };
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin || !isGoogleOidcMessage(event.data)) {
        return;
      }
      if (event.data.source !== GOOGLE_OIDC_MESSAGE_SOURCE || event.data.state !== state) {
        return;
      }
      if (event.data.error !== undefined) {
        finish({
          error: new Error(event.data.errorDescription ?? event.data.error),
        });
        return;
      }
      if (event.data.idToken === undefined || event.data.idToken.trim() === "") {
        finish({ error: new Error("Google did not return an ID token") });
        return;
      }
      if (!jwtPayloadHasNonce(event.data.idToken, nonce)) {
        finish({ error: new Error("Google ID token nonce mismatch") });
        return;
      }
      finish({ token: event.data.idToken });
    };
    popupPoll = window.setInterval(() => {
      const now = Date.now();
      if (popup.closed) {
        closedAt ??= now;
        if (now - closedAt < POPUP_CLOSE_GRACE_MS) {
          return;
        }
        finish({ error: new Error("Google sign-in was cancelled") });
      } else if (now - startedAt > POPUP_TIMEOUT_MS) {
        finish({ error: new Error("Google sign-in timed out") });
      }
    }, POPUP_POLL_INTERVAL_MS);

    window.addEventListener("message", onMessage);
  });
}

function isGoogleOidcMessage(value: unknown): value is GoogleOidcMessage {
  return value !== null && typeof value === "object";
}

function jwtPayloadHasNonce(compactJwt: string, expectedNonce: string): boolean {
  const parts = compactJwt.split(".");
  if (parts.length !== 3 || parts[1] === undefined) {
    return false;
  }

  try {
    const payload = JSON.parse(base64UrlDecodeText(parts[1])) as unknown;
    return (
      payload !== null &&
      typeof payload === "object" &&
      "nonce" in payload &&
      (payload as { nonce?: unknown }).nonce === expectedNonce
    );
  } catch {
    return false;
  }
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function base64UrlDecodeText(value: string): string {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new TextDecoder().decode(bytes);
}

function popupFeatures(): string {
  const width = 480;
  const height = 640;
  const left = Math.max(0, window.screenX + Math.round((window.outerWidth - width) / 2));
  const top = Math.max(0, window.screenY + Math.round((window.outerHeight - height) / 2));

  return [
    "popup=yes",
    "resizable=yes",
    "scrollbars=yes",
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
  ].join(",");
}
